import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Surface } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, Pencil, Beaker, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import AsyncMultiSelect from "@/components/AsyncMultiSelect";
import ComboBoxSearch from "@/components/ComboBoxSearch";

const PAGE_SIZE = 250;

const RM_TYPE_COLORS = {
  "Main Raw Material": "bg-blue-50 text-blue-700 border-blue-200",
  "Stones": "bg-purple-50 text-purple-700 border-purple-200",
  "Paints": "bg-amber-50 text-amber-700 border-amber-200",
  "Decorative": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Others": "bg-gray-100 text-gray-700 border-gray-200",
};

export default function RawMaterials() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [enums, setEnums] = useState({ rm_units: [], rm_types: [] });
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/production/enums").then((r) => setEnums(r.data));
    api.get("/products", { params: { limit: 500 } }).then((r) => setProducts(r.data || []));
    api.get("/categories").then((r) => setCategories(r.data || []));
  }, []);

  // Reset to page 1 whenever any filter changes so the user isn't stranded
  // on a page number that no longer exists in the filtered result set.
  useEffect(() => { setPage(1); }, [search, typeFilter, categoryFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, enrich_products: true };
      if (search) params.search = search;
      if (typeFilter !== "all") params.type = typeFilter;
      if (categoryFilter !== "all") params.category = categoryFilter;
      const res = await api.get("/production/raw-materials", { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, categoryFilter, page]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Category id → name lookup used by the Categories column so we render the
  // actual category label instead of just a count.
  const categoryNameById = React.useMemo(
    () => Object.fromEntries((categories || []).map((c) => [c.id, c.name])),
    [categories]
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input placeholder="Search raw materials…" className="pl-8 h-9 text-sm bg-white" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="rm-search" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-52 h-9 text-sm" data-testid="rm-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {enums.rm_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="w-60">
          <ComboBoxSearch
            value={categoryFilter === "all" ? null : categoryFilter}
            onChange={(v) => setCategoryFilter(v || "all")}
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
            placeholder="All Categories"
            clearable
            testId="rm-category-filter"
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500 tabular-nums" data-testid="rm-total-count">
            {total.toLocaleString()} material{total !== 1 ? "s" : ""}
          </span>
          <Button className="h-9 bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={() => setEditing({ __new: true, name: "", unit: "PCS", description: "", category_ids: [], product_ids: [], type: "Main Raw Material" })} data-testid="rm-add-btn">
            <Plus className="h-4 w-4" /> Add Raw Material
          </Button>
        </div>
      </div>

      <Surface padded={false}>
        <div className="overflow-auto max-h-[calc(100vh-240px)]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">Unit</th>
                <th className="px-4 py-2.5">Vendor</th>
                <th className="px-4 py-2.5">Categories</th>
                <th className="px-4 py-2.5" title="Retail products that list this Raw Material as their Rg parent">Available</th>
                <th className="px-4 py-2.5">Products</th>
                <th className="px-4 py-2.5">Description</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && <tr><td colSpan={9} className="text-center py-8 text-gray-400">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} className="text-center py-12 text-gray-400">
                  <Beaker className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  No raw materials yet — click <span className="font-semibold text-gray-700">Add Raw Material</span>
                </td></tr>
              )}
              {items.map((r) => {
                const catNames = (r.category_ids || [])
                  .map((id) => categoryNameById[id])
                  .filter(Boolean);
                // Fall back to the free-text `category` field (set by Reset
                // from the retail product's category name) when the RM has
                // no resolved category_ids yet — this way the column is
                // never blank for freshly imported materials.
                const catDisplay = catNames.length ? catNames : (r.category ? [r.category] : []);
                const prods = r.manufactured_products || [];
                return (
                <tr key={r.id} className="hover:bg-gray-50 align-top" data-testid={`rm-row-${r.id}`}>
                  <td className="px-4 py-2 font-medium text-gray-900">{r.name}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded border text-[11px] font-medium ${RM_TYPE_COLORS[r.type] || RM_TYPE_COLORS.Others}`}>{r.type}</span>
                  </td>
                  <td className="px-4 py-2 text-gray-600">{r.unit}</td>
                  <td className="px-4 py-2 text-gray-700" data-testid={`rm-vendor-${r.id}`}>
                    {r.vendor_name && r.vendor_name.toLowerCase() !== "nil" ? (
                      <span className="inline-block bg-slate-50 border border-slate-200 text-slate-800 px-1.5 py-0.5 rounded text-[11px] leading-tight">
                        {r.vendor_name}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">Unassigned</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-700 max-w-[220px]" data-testid={`rm-cat-${r.id}`}>
                    {catDisplay.length === 0 ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {catDisplay.map((n) => (
                          <span key={n} className="inline-block bg-blue-50 border border-blue-200 text-blue-800 px-1.5 py-0.5 rounded text-[11px] leading-tight">
                            {n}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-800 tabular-nums font-semibold" data-testid={`rm-mfd-count-${r.id}`}>
                    {r.manufactured_count > 0 ? (
                      <span className="inline-flex items-center rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5 text-[11px] font-bold">
                        {r.manufactured_count}
                      </span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-700 max-w-[320px]" data-testid={`rm-products-${r.id}`}>
                    {prods.length === 0 ? (
                      <span className="text-gray-400 text-xs">—</span>
                    ) : (
                      <div
                        className="flex flex-wrap gap-1"
                        title={prods.map((p) => p.name).join(", ")}
                      >
                        {prods.slice(0, 3).map((p) => (
                          <span key={p.id} className="inline-block bg-emerald-50 border border-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded text-[11px] leading-tight truncate max-w-[140px]">
                            {p.name}
                          </span>
                        ))}
                        {prods.length > 3 && (
                          <span className="inline-block bg-gray-100 border border-gray-200 text-gray-700 px-1.5 py-0.5 rounded text-[11px] leading-tight font-semibold">
                            +{prods.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-500 truncate max-w-xs">{r.description || "—"}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setEditing({ ...r })} className="inline-flex items-center gap-1 h-7 px-2 rounded hover:bg-gray-100 text-xs text-gray-700" data-testid={`rm-edit-${r.id}`}>
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Surface>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-gray-600" data-testid="rm-pagination">
          <span>
            Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="rm-page-prev" className="h-8 gap-1">
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </Button>
            <span className="tabular-nums font-medium text-gray-700" data-testid="rm-page-info">
              Page {page} of {totalPages}
            </span>
            <Button size="sm" variant="outline" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)} data-testid="rm-page-next" className="h-8 gap-1">
              Next <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {editing && (
        <RMEditor
          initial={editing}
          enums={enums}
          products={products}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ManufacturedProductsPanel({ rmId }) {
  const [state, setState] = useState({ loading: true, data: null });
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/production/raw-materials/${rmId}/manufactured-products`);
        setState({ loading: false, data: res.data });
      } catch {
        setState({ loading: false, data: null });
      }
    })();
  }, [rmId]);
  if (state.loading) {
    return (
      <div className="mt-4 rounded-md bg-gray-50 border border-gray-200 px-3 py-4 text-xs text-gray-500 flex items-center gap-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading manufactured products…
      </div>
    );
  }
  const d = state.data;
  if (!d) return null;
  if (d.is_manual) {
    return (
      <div className="mt-4 rounded-md bg-gray-50 border border-gray-200 px-3 py-3 text-xs text-gray-500">
        This Raw Material was added manually and is not linked to a Retail product.
        Retail products can&apos;t map it as a Rg parent until you re-run the Production Reset from Settings.
      </div>
    );
  }
  return (
    <div className="mt-4 rounded-md bg-amber-50/40 border border-amber-200 p-3">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-800 mb-2">
        Products This Material Can Manufacture <span className="text-amber-700">({d.products.length})</span>
      </p>
      {d.products.length === 0 ? (
        <p className="text-xs text-gray-500 italic">
          No Retail products currently list this Raw Material as their Rg parent yet.
        </p>
      ) : (
        <div className="max-h-56 overflow-y-auto space-y-1">
          {d.products.map((p) => (
            <div key={p.id} className="flex items-center justify-between text-xs bg-white rounded border border-amber-100 px-2 py-1.5">
              <div className="min-w-0">
                <p className="font-medium text-gray-900 truncate">{p.name}</p>
                <p className="text-[10px] text-gray-500 font-mono truncate">{p.code || p.wings_product_id}</p>
              </div>
              <span className={`font-mono font-bold ${p.total_stock < 0 ? "text-red-600" : "text-amber-800"}`}>
                {p.total_stock}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RMEditor({ initial, enums, products, categories, onClose, onSaved }) {
  const isNew = !!initial.__new;
  const [form, setForm] = useState({
    name: initial.name || "",
    unit: initial.unit || "PCS",
    description: initial.description || "",
    hsn_code: initial.hsn_code || "",
    category_ids: initial.category_ids || [],
    product_ids: initial.product_ids || [],
    type: initial.type || "Main Raw Material",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Pre-resolved label caches so chips render immediately from the initial arrays.
  const categoryLabels = React.useMemo(
    () => Object.fromEntries((categories || []).map((c) => [c.id, c.name])),
    [categories]
  );
  const productLabels = React.useMemo(
    () => Object.fromEntries((products || []).map((p) => [p.id, p.name])),
    [products]
  );

  const searchCategories = async (q) => {
    const list = (categories || []).filter((c) =>
      !q || c.name.toLowerCase().includes(q.toLowerCase())
    );
    return list.slice(0, 25).map((c) => ({ id: c.id, label: c.name }));
  };
  const searchProducts = async (q) => {
    const params = { limit: 25 };
    if (q && q.trim().length >= 3) params.search = q.trim();
    const res = await api.get("/products", { params });
    return (res.data || []).map((p) => ({ id: p.id, label: p.name, hint: p.code || p.wings_product_id }));
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/production/raw-materials", form);
        toast.success("Raw material created");
      } else {
        await api.put(`/production/raw-materials/${initial.id}`, form);
        toast.success("Raw material updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "New Raw Material" : `Edit — ${initial.name}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="rm-name" /></Field>
            <Field label="Type *">
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="rm-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {enums.rm_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Unit *">
              <Select value={form.unit} onValueChange={(v) => set("unit", v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="rm-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {enums.rm_units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="HSN / SAC Code"><Input value={form.hsn_code} onChange={(e) => set("hsn_code", e.target.value.toUpperCase())} className="font-mono" data-testid="rm-hsn" placeholder="e.g. 7113" /></Field>
            <Field label="Description" className="col-span-2">
              <textarea className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 min-h-[70px]" value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="rm-description" />
            </Field>
            <Field label="Categories (Product)" className="col-span-2">
              <AsyncMultiSelect
                value={form.category_ids}
                valueLabels={categoryLabels}
                onChange={(v) => set("category_ids", v)}
                fetchOptions={searchCategories}
                placeholder="Search & add categories…"
                emptyLabel="Type to search categories…"
                testId="rm-categories"
              />
            </Field>
            <Field label="Products this material can manufacture" className="col-span-2">
              <AsyncMultiSelect
                value={form.product_ids}
                valueLabels={productLabels}
                onChange={(v) => set("product_ids", v)}
                fetchOptions={searchProducts}
                placeholder="Search & add products (type 3+ chars)…"
                emptyLabel="Type 3+ characters to search products…"
                testId="rm-products"
              />
            </Field>
          </div>

          {/* Reverse-lookup panel: Retail products that map THIS RM as their Rg parent */}
          {!isNew && initial.id && <ManufacturedProductsPanel rmId={initial.id} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D]" onClick={save} disabled={saving} data-testid="rm-save">
            {saving ? "Saving…" : (isNew ? "Create Material" : "Save Changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
